import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload } from 'lucide-react'
import { documentsApi } from '../api'
import { useToastStore } from '../store/toast'

interface FileUploadProps {
  onUploadComplete?: () => void
}

export default function FileUpload({ onUploadComplete }: FileUploadProps) {
  const [uploading, setUploading] = useState(false)
  const addToast = useToastStore((state) => state.addToast)

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const files = acceptedFiles
    if (!files.length) return
    const invalid = files.find((file) => file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf'))
    if (invalid) {
      addToast({ type: 'error', message: 'Only PDF files are supported in this version.' })
      return
    }
    if (files.some((file) => file.size > 25 * 1024 * 1024)) {
      addToast({ type: 'error', message: 'File size exceeds 25MB.' })
      return
    }

    setUploading(true)
    try {
      if (files.length === 1) {
        const response = await documentsApi.upload(files[0])
        addToast({
          type: response.data.duplicate_of ? 'warning' : 'success',
          message: response.data.duplicate_of
            ? `Duplicate detected. Existing document: ${response.data.duplicate_of.original_filename}`
            : `Uploaded ${files[0].name}`,
        })
      } else {
        const response = await documentsApi.uploadBatch(files)
        const uploaded = response.data.count
        const failed = response.data.errors.length
        addToast({
          type: failed ? 'warning' : 'success',
          message: failed
            ? `Uploaded ${uploaded} PDFs. ${failed} failed or need attention.`
            : `Uploaded ${uploaded} PDFs.`,
        })
      }
      onUploadComplete?.()
    } catch (err: any) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Upload failed.' })
    } finally {
      setUploading(false)
    }
  }, [addToast, onUploadComplete])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 25,
    maxSize: 25 * 1024 * 1024,
  })

  return (
    <div
      {...getRootProps()}
      className={`border border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
        isDragActive ? 'border-brand-blue bg-brand-light/60' : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
      } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
    >
      <input {...getInputProps()} disabled={uploading} />
      <Upload className="w-10 h-10 text-brand-blue mx-auto mb-4" />
      <p className="font-medium text-gray-950">{uploading ? 'Uploading...' : isDragActive ? 'Drop PDFs here' : 'Drag PDFs here, or click to select'}</p>
      <p className="text-sm text-gray-500 mt-1">PDF only, max 25MB each. Batch upload is available for Pro and Advanced.</p>
    </div>
  )
}
